import type { IDialogProps, IOmit } from '@sixui/ui';
import { Button, Dialog, Labeled, TextInputField } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IUsePersistentFormResult } from '~/components/utils/PersistentForm';
import type { IFormMode } from '~/helpers/types';
import type {
  IArticleFormSubmitAction,
  IArticleFormValues,
} from './ArticleForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';

export type INotifyArticleDialogProps = IOmit<
  IDialogProps,
  'headline' | 'actions' | 'children'
> & {
  mode: IFormMode;
  form: IUsePersistentFormResult<IArticleFormValues>;
  onSubmit?: (
    values: IArticleFormValues,
    event?: React.BaseSyntheticEvent,
    action?: IArticleFormSubmitAction,
  ) => void;
};

export const NotifyArticleDialog: React.FC<INotifyArticleDialogProps> = (
  props,
) => {
  const { mode, form, onSubmit, ...other } = props;
  const { t } = useTranslation();

  return (
    <Dialog
      headline={t('NotifyArticleDialog.title')}
      actions={({ close }) => (
        <>
          <Button
            variant="text"
            onClick={close}
            disabled={form.formState.isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={
              onSubmit
                ? form.handleSubmit((values, event) =>
                    onSubmit(values, event, 'publishAndNotify'),
                  )
                : undefined
            }
          >
            {mode === 'creation'
              ? t('NotifyArticleDialog.publish-now-and-notify')
              : t('NotifyArticleDialog.update-and-notify')}
          </Button>
        </>
      )}
      {...other}
    >
      <FormSection>
        <Labeled label={t('NotifyArticleDialog.field.title.label')} required>
          <FormField
            name="notificationTitle"
            control={form.control}
            as={TextInputField}
            forwardError
          />
        </Labeled>
        <Labeled
          label={t('NotifyArticleDialog.field.body.label')}
          supportingText={t('NotifyArticleDialog.field.body.supporting-text')}
        >
          <FormField
            name="notificationBody"
            control={form.control}
            as={TextInputField}
            forwardError
          />
        </Labeled>

        {!form.formState.isValid ? (
          <ErrorMessage error={t('NotifyArticleDialog.error.form-has-error')} />
        ) : null}
      </FormSection>
    </Dialog>
  );
};
